const BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

interface ApiErrorBody {
  message: string;
  code?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = "ApiError";
  }
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

function getTokens(): TokenPair | null {
  const accessToken = localStorage.getItem("alice_access_token");
  const refreshToken = localStorage.getItem("alice_refresh_token");
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

function setTokens(tokens: TokenPair) {
  localStorage.setItem("alice_access_token", tokens.accessToken);
  localStorage.setItem("alice_refresh_token", tokens.refreshToken);
}

export function clearTokens() {
  localStorage.removeItem("alice_access_token");
  localStorage.removeItem("alice_refresh_token");
}

let refreshPromise: Promise<TokenPair> | null = null;

async function refreshAccessToken(): Promise<TokenPair> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const tokens = getTokens();
    if (!tokens?.refreshToken) {
      throw new ApiError(401, { message: "No refresh token" });
    }

    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });

    if (!res.ok) {
      clearTokens();
      throw new ApiError(res.status, { message: "Token refresh failed" });
    }

    const data = (await res.json()) as TokenPair;
    setTokens(data);
    return data;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

type RequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T>(
  method: RequestMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const tokens = getTokens();

  let url = `${BASE_URL}${path}`;
  if (options.params) {
    const qs = new URLSearchParams(options.params).toString();
    url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (tokens?.accessToken) {
    headers["Authorization"] = `Bearer ${tokens.accessToken}`;
  }

  let res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal: options.signal,
  });

  // If 401 and we have a refresh token, try refreshing
  if (res.status === 401 && tokens?.refreshToken) {
    try {
      const newTokens = await refreshAccessToken();
      headers["Authorization"] = `Bearer ${newTokens.accessToken}`;
      res = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: options.signal,
      });
    } catch {
      clearTokens();
      window.location.href = "/login";
      throw new ApiError(401, { message: "Session expired" });
    }
  }

  if (!res.ok) {
    let errorBody: ApiErrorBody;
    try {
      const raw = await res.json();
      // API may return { error: "..." } or { message: "..." } — normalize
      errorBody = {
        ...raw,
        message: raw.message ?? raw.error ?? res.statusText,
      };
    } catch {
      errorBody = { message: res.statusText };
    }
    throw new ApiError(res.status, errorBody);
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string, options?: RequestOptions) {
    return request<T>("GET", path, undefined, options);
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>("POST", path, body, options);
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>("PUT", path, body, options);
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>("PATCH", path, body, options);
  },
  delete<T>(path: string, options?: RequestOptions) {
    return request<T>("DELETE", path, undefined, options);
  },
  setTokens,
  getTokens,
  clearTokens,
};
